import { T, t } from '@app/components/translation';
import { useState, useRef, useEffect } from 'react';
import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getCurrentLanguageSelector } from '@app/reducers/i18n';
import { usePlaybookStore } from '@app/store/zustand-store/use-playbook-store';

import composeValidators from '@flow/libs/forms/validators/compose-validators';
import required from '@flow/libs/forms/validators/required';
import numberValidator from '@flow/libs/forms/validators/number';
import minValue from '@flow/libs/forms/validators/min-value';
import { getCountryListAsOptions, getAmericanStatesAsOptions } from '@app/components/countries';
import SelectField from '@app/components/select-field';
import { getGoverningLawTranslation } from '@app/components/pages/ai-insights/charts/utils';
import Delete from '@app/components/icons/delete';
import Button from '@app/components/button';
import { NumberField } from '@app/components/text-field';
import { FormLabel } from '@app/components/form';
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectIcon,
} from '@app/components/select-compound';
import toast from '@app/components/toasts';

import { EditModal } from './edit-modal';
import style from './concept-card.module.scss';
import { CRITICAL, INFO, WARNING, FOUND, MISSING, GREATER_THAN, LESS_THAN } from '../constants';

const valueOperatorOptions = [
  {
    value: LESS_THAN,
    label: (
      <T
        _str="Less than or equal to"
        _comment="Option in the payment term selector."
      />
    ),
  },
  {
    value: GREATER_THAN,
    label: (
      <T
        _str="More than or equal to"
        _comment="Option in the payment term selector."
      />
    ),
  },
];

const getCustomFields = (
  name: string,
  value: string | string[] | undefined,
  currentLanguage: string,
) => {
  if (!value) {
    return null;
  }

  if (name === 'disputeResolution') {
    return (
      <>
        <div className={style.LabelModal}>
          <T
            _str="Arbitration"
            _comment="Label for the dispute resolution input."
          />
        </div>
        <div className={style.ArbitrationInputContainer}>
          <Field
            type="radio"
            value="LABEL_ARBITRATION"
            name="disputeResolution"
            id="arbitration-accept"
            className={style.RadioButton}
            component="input"
          />
          <FormLabel
            className={style.FormLabel}
            htmlFor="arbitration-accept"
          >
            <T
              _str="Yes, I accept arbitration as a dispute resolution"
              _comment="Checkbox label in the AI Review setup page."
            />
          </FormLabel>
        </div>
        <div className={style.ArbitrationInputContainer}>
          <Field
            type="radio"
            value="LABEL_DECLINE"
            name="disputeResolution"
            id="arbitration-decline"
            className={style.RadioButton}
            component="input"
          />
          <FormLabel
            className={style.FormLabel}
            htmlFor="arbitration-decline"
          >
            <T
              _str="No, I do not accept arbitration as a dispute resolution"
              _comment="Checkbox label in the AI Review setup page."
            />
          </FormLabel>
        </div>
      </>
    );
  }

  if (name === 'governingLaw') {
    const governingLawOptions = [
      {
        value: 'EU',
        label: t('European Union', {
          _comment: 'Option in the governing law selector',
        }),
      },
      {
        value: 'NORD',
        label: t('Nordic countries', {
          _comment: 'Option in the governing law selector',
        }),
      },
      {
        value: 'GB-ENG',
        label: t('England', {
          _comment: 'Option in the governing law selector',
        }),
      },
      {
        value: 'GB-SCT',
        label: t('Scotland', {
          _comment: 'Option in the governing law selector',
        }),
      },
      {
        value: 'GB-WLS',
        label: t('Wales', {
          _comment: 'Option in the governing law selector',
        }),
      },
      {
        value: 'GB-NIR',
        label: t('Northern Ireland', {
          _comment: 'Option in the governing law selector',
        }),
      },
      ...getAmericanStatesAsOptions(),
      ...getCountryListAsOptions(currentLanguage),
    ];

    return (
      <>
        <div className={style.LabelModal}>
          <T
            _str="Jurisdictions"
            _comment="Label for the governing law input."
          />
          <span className={style.Required}>*</span>
        </div>
        <Field
          name="governingLaw"
          value={value.map((countryCode: string) => ({
            value: countryCode,
            label: getGoverningLawTranslation(countryCode, currentLanguage),
          }))}
          component={SelectField}
          options={governingLawOptions}
          placeholder={t('Select countries and regions', {
            _comment: 'Placeholder in the AI Review page.',
          })}
          multi
          hideLabel
          closeOnSelect={false}
          validate={composeValidators(
            required({
              field: 'governingLaw',
              text: t('Please select at least one governing law.', {
                _comment: 'Validation message for governing law selection.',
              }),
            }),
          )}
        />
      </>
    );
  }

  if (name === 'paymentTerm') {
    const text = t('Please enter a whole number greater than 1.', {
      _comment: 'Validation message',
    });
    const paymentTermValidations = composeValidators(
      numberValidator({
        field: 'paymentTerm',
        text,
      }),
      minValue({
        field: 'paymentTerm',
        text,
        limit: 1,
      }),
      required({
        field: 'paymentTerm',
        text,
      }),
    );
    return (
      <>
        <FormLabel
          className={style.LabelModal}
          htmlFor="reviewType"
        >
          <T
            _str="Payment term time frame"
            _comment="label for review type field in edit playbook modal."
          />
          <span className={style.Required}>*</span>
        </FormLabel>
        <div className={style.PaymentTermContainer}>
          <Field
            name="valueOperator"
            label={t('Payment term value operator', {
              _comment: 'Label for the payment term value operator input',
            })}
            hideLabel
            required
            render={({ input: { onChange, value } }) => (
              <Select
                value={value || undefined}
                onValueChange={onChange}
              >
                <SelectTrigger
                  id="valueOperator"
                  className={style.SelectTrigger}
                >
                  <SelectValue />
                  <SelectIcon />
                </SelectTrigger>
                <SelectContent
                  style={{
                    zIndex: 10002,
                  }}
                >
                  {valueOperatorOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <Field
            name="paymentTerm"
            component={NumberField}
            value={parseISODuration(value)}
            validate={paymentTermValidations}
            customClass={style.PaymentTermInput}
            min="1"
          />
          <span className={style.PaymentTermDays}>
            <T
              _str="days"
              _comment="Label for the payment term input."
            />
          </span>
        </div>
      </>
    );
  }
};

type Props = {
  name: string;
  values: {
    enabled: boolean;
    feedbackTextExample: string;
    feedbackTextWhatToDo: string;
    breachSeverityWhenFound: string;
    breachSeverityWhenMissing: string;
    value?: string | string[];
    valueOperator?: string; // Optional, used for paymentTerm
  };
  areActionsVisible: boolean;
};

const playbookParams: Record<string, any> = {
  paymentTerm: {
    label: (
      <T
        _str="Payment term"
        _comment="Label for the payment term clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="A payment term outlines the time frame in which a party is required to pay the invoice."
        _comment="Description of the payment term clause."
      />
    ),
  },
  governingLaw: {
    label: (
      <T
        _str="Governing law"
        _comment="Label for the governing law clause in the playbook configuration."
      />
    ),
    description: (
      <>
        <T
          _str="A governing law clause explains which law will govern the contract, which is crucial for dispute interpretation."
          _comment="Description of the governing law clause."
        />
        <div
          style={{
            margin: '8px 0 20px 0',
          }}
        >
          <T
            _str="Select countries and regions that you accept in your documents."
            _comment="Description of the governing law clause."
          />
        </div>
      </>
    ),
  },
  disputeResolution: {
    label: (
      <T
        _str="Dispute resolution"
        _comment="Label for the dispute resolution clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="A dispute resolution clause outlines the process for resolving disputes between parties."
        _comment="Description of the dispute resolution clause."
      />
    ),
  },
  confidentiality: {
    label: (
      <T
        _str="Confidentiality"
        _comment="Label for the confidentiality clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of confidentiality clauses to protect sensitive information."
        _comment="Description of the confidentiality clause."
      />
    ),
  },
  exclusionOfConsequentialLoss: {
    label: (
      <T
        _str="Exclusion of Consequential Damages"
        _comment="Label for the exclusion of consequential damages clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of clauses that exclude consequential losses."
        _comment="Description of the exclusion of consequential damages clause."
      />
    ),
  },
  forceMajeure: {
    label: (
      <T
        _str="Force Majeure"
        _comment="Label for the force majeure clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of force majeure clauses that cover unforeseen events."
        _comment="Description of the force majeure clause."
      />
    ),
  },
  indemnities: {
    label: (
      <T
        _str="Indemnities"
        _comment="Label for the indemnities clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of indemnity clauses that specify compensation for losses or damages."
        _comment="Description of the indemnities clause."
      />
    ),
  },
  limitationOfLiability: {
    label: (
      <T
        _str="Limitation of Liability"
        _comment="Label for the limitation of liability clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of clauses that limit liability."
        _comment="Description of the limitation of liability clause."
      />
    ),
  },
  liquidatedDamages: {
    label: (
      <T
        _str="Liquidated Damages"
        _comment="Label for the liquidated damages clause in the playbook configuration."
      />
    ),
    description: (
      <T
        _str="Check for the presence or absence of liquidated damages clauses that predefine compensation for breaches."
        _comment="Description of the liquidated damages clause."
      />
    ),
  },
};

const parseISODuration = (duration: string): number => {
  const match = duration.match(/P(\d+)D/);
  if (match && match[1]) {
    return parseInt(match[1], 10);
  }
  return 0;
};

export const ConceptCard = ({ name, values, areActionsVisible }: Props) => {
  const currentLanguage = useSelector(getCurrentLanguageSelector);
  const setPlaybookValues = usePlaybookStore((state) => state.setPlaybookValues);
  const [isExampleExpanded, setExampleExpanded] = useState(false);
  const [isFeedbackExpanded, setFeedbackExpanded] = useState(false);
  const feedbackRef = useRef<HTMLSpanElement>(null);
  const exampleRef = useRef<HTMLSpanElement>(null);
  const [isFeedbackTruncated, setIsFeedbackTruncated] = useState(false);
  const [isExampleTruncated, setIsExampleTruncated] = useState(false);

  const initialReviewType = values.breachSeverityWhenFound === INFO ? FOUND : MISSING;

  // biome-ignore lint/correctness/useExhaustiveDependencies: Needs to run when texts change
  useEffect(() => {
    if (feedbackRef.current && !isFeedbackExpanded) {
      setIsFeedbackTruncated(
        feedbackRef.current.scrollHeight > feedbackRef.current.clientHeight + 1,
      );
    }
    if (exampleRef.current && !isExampleExpanded) {
      setIsExampleTruncated(exampleRef.current.scrollHeight > exampleRef.current.clientHeight + 1);
    }
  }, [
    isFeedbackExpanded,
    isExampleExpanded,
    values.feedbackTextWhatToDo,
    values.feedbackTextExample,
  ]);

  const renderPlaybookValue = (
    name: string,
    value: any,
    valueOperator?: string,
  ): React.ReactElement | null => {
    if (values.breachSeverityWhenMissing === INFO) {
      return null;
    }

    if (name === 'governingLaw') {
      return (
        <div
          data-testid="governing-law"
          style={{
            margin: '16px 0',
          }}
        >
          <p className={style.Subheader}>
            <T
              _str="Jurisdictions"
              _comment="Header for presenting the value of legal jurisdictions chosen by the user."
            />
          </p>
          <span>
            {value
              .map((country: string) => getGoverningLawTranslation(country, currentLanguage))
              .join(', ')}
          </span>
        </div>
      );
    }
    if (name === 'disputeResolution') {
      return (
        <div
          data-testid="dispute-resolution"
          style={{
            margin: '16px 0',
          }}
        >
          <p
            style={{
              fontWeight: 500,
              marginBottom: 0,
            }}
          >
            <T
              _str="Arbitration"
              _comment="Label for the arbitration clause, indicating the dispute resolution method chosen by the user."
            />
          </p>
          <span>
            {value.includes('LABEL_ARBITRATION') ? (
              <T
                _str="Yes, I accept arbitration as a dispute resolution"
                _comment="Text indicating that the user accepts arbitration as the preferred method for resolving disputes."
              />
            ) : (
              <T
                _str="No, I do not accept arbitration as a dispute resolution"
                _comment="Text indicating that the user does not accept arbitration as the preferred method for resolving disputes."
              />
            )}
          </span>
        </div>
      );
    }
    if (name === 'paymentTerm') {
      return (
        <div
          data-testid="payment-term"
          style={{
            margin: '16px 0',
          }}
        >
          <p
            style={{
              fontWeight: 500,
              marginBottom: 0,
            }}
          >
            <T
              _str="Payment term time frame"
              _comment="Label for the payment term clause, specifying the time frame within which payments must be made."
            />
          </p>
          <div>
            <span>
              {valueOperatorOptions.find((option) => option.value === valueOperator)?.label || ''}
            </span>
            &nbsp;
            <T
              _str="{numberOfDays} days"
              _comment="Text displaying the payment term duration in days, including the operator (e.g., 'more than', 'less than')."
              valueOperator={valueOperator}
              numberOfDays={parseISODuration(value)}
            />
          </div>
        </div>
      );
    }
    return null;
  };

  const disableConcept = () => {
    setPlaybookValues({
      [name]: {
        enabled: false,
      },
    });
    toast.success({
      id: 'clause-removed',
      title: (
        <T
          _str="Clause removed"
          _comment="Toast message when a clause is successfully removed."
        />
      ),
      duration: 5000,
    });
  };

  if (!name || !playbookParams[name]) {
    return null;
  }

  const convertValueToFieldFormat = (key: string, value: string | string[]) => {
    if (key === 'paymentTerm') {
      return parseISODuration(value);
    }
    if (key === 'governingLaw') {
      return value.map((country: string) => ({
        value: country,
        label: getGoverningLawTranslation(country, currentLanguage),
      }));
    }
    if (key === 'disputeResolution') {
      return value[0] || 'LABEL_DECLINE';
    }
    return value;
  };

  const renderShowMoreButton = (isExpanded: boolean, setExpanded: (arg0: boolean) => void) => (
    <button
      className={style.ExpandButton}
      onClick={() => setExpanded(!isExpanded)}
    >
      {isExpanded ? (
        <T
          _str="Show less"
          _comment="show more content of text', used as button text."
        />
      ) : (
        <T
          _str="Show more"
          _comment="show less content of text', used as button text."
        />
      )}
    </button>
  );

  return (
    <div className={style.Card}>
      <h2 className={style.CardTitle}>{playbookParams[name].label}</h2>
      <div
        style={{
          margin: '24px 0',
        }}
      >
        <p
          style={{
            marginBottom: 12,
          }}
          className={style.Subheader}
        >
          <T
            _str="Feedback text"
            _comment="Header for the feedback text section."
          />
        </p>
        <span
          data-testid="feedback-text"
          ref={feedbackRef}
          className={isFeedbackExpanded ? undefined : style.TruncateEllipsis}
        >
          {values.feedbackTextWhatToDo || '-'}
        </span>
        {isFeedbackTruncated && renderShowMoreButton(isFeedbackExpanded, setFeedbackExpanded)}
      </div>
      {values.breachSeverityWhenFound === INFO && (
        <div
          data-testid="example-clause"
          style={{
            margin: '24px 0',
          }}
        >
          <p
            style={{
              marginBottom: 12,
            }}
            className={style.Subheader}
          >
            <T
              _str="Example clause text"
              _comment="Header for the example clause text section."
            />
          </p>
          <span
            data-testid="example-clause-text"
            ref={exampleRef}
            className={isExampleExpanded ? undefined : style.TruncateEllipsis}
          >
            {values.feedbackTextExample || '-'}
          </span>
          {isExampleTruncated && renderShowMoreButton(isExampleExpanded, setExampleExpanded)}
        </div>
      )}
      <div>
        <hr />
        <div
          style={{
            margin: '16px 0',
          }}
        >
          {values.breachSeverityWhenFound === INFO && (
            <div data-testid="severity-found">
              <p className={style.Subheader}>
                <T
                  _str="Severity if clause isn't present in document"
                  _comment="Header for the clause breach severity."
                />
              </p>
              {values.breachSeverityWhenMissing === WARNING && (
                <p data-testid="severity-warning">
                  <T
                    _str="Risk"
                    _comment="Label for when a breaching clause in a document is a critical risk to the user."
                  />
                </p>
              )}
              {values.breachSeverityWhenMissing === CRITICAL && (
                <p data-testid="severity-critical">
                  <T
                    _str="Issue"
                    _comment="Label for when a breaching clause in a document is a small risk to the user."
                  />
                </p>
              )}
            </div>
          )}
          {values.breachSeverityWhenMissing === INFO && (
            <div data-testid="severity-missing">
              <p className={style.Subheader}>
                <T
                  _str="Severity if clause is present in document"
                  _comment="Header for the clause breach severity."
                />
              </p>
              {values.breachSeverityWhenFound === WARNING && (
                <p data-testid="severity-warning">
                  <T
                    _str="Risk"
                    _comment="Label for when a breaching clause in a document is a critical risk to the user."
                  />
                </p>
              )}
              {values.breachSeverityWhenFound === CRITICAL && (
                <p data-testid="severity-critical">
                  <T
                    _str="Issue"
                    _comment="Label for when a breaching clause in a document is a small risk to the user."
                  />
                </p>
              )}
            </div>
          )}
        </div>
        {values.value && (
          <span>{renderPlaybookValue(name, values.value, values.valueOperator)}</span>
        )}
      </div>
      {areActionsVisible && (
        <>
          <hr />
          <div
            data-testid="concept-card-actions"
            className={style.ActionsContainer}
          >
            <EditModal
              name={name}
              title={playbookParams[name].label}
              initialValues={{
                ...(values.value && {
                  [name]: convertValueToFieldFormat(name, values.value),
                }),
                ...(values.valueOperator && {
                  valueOperator: values.valueOperator,
                }),
                feedbackTextWhatToDo: values.feedbackTextWhatToDo,
                feedbackTextExample: values.feedbackTextExample,
                breachSeverityWhenFound: values.breachSeverityWhenFound,
                breachSeverityWhenMissing: values.breachSeverityWhenMissing,
                reviewType: initialReviewType,
              }}
            >
              {getCustomFields(name, values.value, currentLanguage)}
            </EditModal>
            <Button
              icon={Delete}
              onClick={disableConcept}
            >
              <T
                _str="Remove"
                _comment="Button label for removing a clause from the playbook settings"
              />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
